import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, useBranding } from '@/App';
import { LayoutDashboard, Boxes, Key, BarChart3, CreditCard, BookOpen, Terminal, Settings, Users, FileText, Cpu, Shield, LogOut, ChevronLeft, Globe, Rocket, Eye, PieChart, ScrollText, AlertCircle, Server, Gauge, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const clientNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: '/dashboard/getting-started', icon: Rocket, label: 'Getting Started' },
  { to: '/dashboard/services', icon: Boxes, label: 'Services' },
  { to: '/dashboard/api-keys', icon: Key, label: 'API Keys' },
  { to: '/dashboard/usage', icon: BarChart3, label: 'Usage' },
  { to: '/dashboard/billing', icon: CreditCard, label: 'Billing' },
  { to: '/dashboard/docs', icon: BookOpen, label: 'API Docs' },
  { to: '/dashboard/playground', icon: Terminal, label: 'Playground' },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
];

const adminNav = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/config', icon: Settings, label: 'Config Editor' },
  { to: '/admin/landing', icon: Globe, label: 'Landing Page' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/reports', icon: PieChart, label: 'Reports' },
  { to: '/admin/analytics', icon: Eye, label: 'Page Analytics' },
  { to: '/admin/logs', icon: FileText, label: 'Audit Logs' },
  { to: '/admin/system', icon: Cpu, label: 'GPU Health' },
  { to: '/admin/gpu-logs', icon: ScrollText, label: 'GPU Logs' },
  { to: '/admin/failed-requests', icon: AlertCircle, label: 'Failed Requests' },
  { to: '/admin/servers', icon: Server, label: 'GPU Servers' },
  { to: '/admin/customer-assignments', icon: Network, label: 'Customer Mapping' },
  { to: '/admin/tier-limits', icon: Gauge, label: 'Tier Limits' },
];

export default function DashboardLayout({ isAdmin = false }) {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const navItems = isAdmin ? adminNav : clientNav;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div data-testid="dashboard-layout" className="flex h-screen overflow-hidden bg-background">
      <aside className="w-64 flex flex-col border-r border-border/50 bg-card/50 backdrop-blur-sm">
        <div className="p-5 flex items-center gap-3">
          <img src={branding.logo_url || '/tensornex-logo.png'} alt={branding.brand_name || 'Tensornex'} className="w-8 h-8 object-contain" />
          <div>
            <h1 className="text-sm font-bold tracking-tight" style={{fontFamily: "'Outfit', sans-serif"}}>
              {isAdmin ? 'Admin Panel' : (branding.brand_name || 'Tensornex')}
            </h1>
            <p className="text-xs text-muted-foreground truncate max-w-[160px]">{user?.email}</p>
          </div>
        </div>
        <Separator />
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-primary/15 text-primary shadow-[0_0_10px_rgba(99,102,241,0.15)]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>
        <Separator />
        <div className="p-3 space-y-2">
          {isAdmin ? (
            <NavLink to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors">
              <ChevronLeft className="w-3 h-3" />
              Client Dashboard
            </NavLink>
          ) : user?.is_admin ? (
            <NavLink to="/admin" className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground rounded-lg hover:bg-secondary/50 transition-colors">
              <Shield className="w-3 h-3" />
              Admin Panel
            </NavLink>
          ) : null}
          <div className="flex items-center justify-end px-3 py-1">
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="h-7 text-xs text-muted-foreground hover:text-destructive">
              <LogOut className="w-3 h-3 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-7xl mx-auto animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
